import {
  BookmarkIcon,
  ClipboardDocumentIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { LoadingButton } from '@mui/lab';
import { Container, Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import BackButton from '@straico-ui/commons/BackButton';
import ModalWithTextField from 'Components/Common/Modal/ModalApi';
import useClipboardComponent from 'Hooks/useClipboardComponent';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';

const ApiSettings = ({ store, onSubmitSuccess }) => {
  /** THEME */
  const { theme } = useThemeStore();

  /** NAVIGATION */
  const navigate = useNavigate();

  /** PROFILE INFO */
  const { api_key } = store.profile;

  /** STATES */
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadingResetApiKey, setLoadingResetApiKey] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);

  const { copy } = useClipboardComponent();

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleCopy = (e) => {
    try {
      e.preventDefault();
      copy(store.profile.api_key);
      toast.success('API key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopy2 = (e, apiKeyValue) => {
    try {
      e.preventDefault();
      copy(apiKeyValue);
      toast.success('API key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const resetApiKey = async () => {
    setLoadingResetApiKey(true);
    try {
      const res = await store.api.post(`/user/change-api-key`, {});
      if (res.status === 200) {
        store.profile.api_key = res.data.api_key;
        toast.success('API key updated');
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        toast.error('Updating error');
      }
    } catch (error) {
      toast.error('Updating error');
    } finally {
      setLoadingResetApiKey(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await store.api.get('key/api-keys');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API Keys:', error);
    }
  };

  const deleteKeyById = async (keyId) => {
    try {
      const response = await store.api.delete(`key/${keyId}`);
      if (response.status === 200) {
        fetchApiKeys();
        toast.success('Key deleted');
      } else {
        console.warn('Could not delete key. Response status:', response.status);
      }
    } catch (error) {
      toast.error('Error deleting key');
    }
  };

  const addNewKey = async (textFieldValue) => {
    try {
      const createKeyResponse = await store.api.post('key/create', {
        name: textFieldValue,
      });
      if (createKeyResponse.status === 201) {
        fetchApiKeys();
        toast.success('New key created');
      } else {
        throw new Error('Error creating new key');
      }
    } catch (error) {
      toast.error('AddNewKey error: ' + error.message);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  return (
    <div className="overflow-auto">
      <Container maxWidth="md" className="font-figtree">
        <BackButton
          text="Go back to user settings"
          onClick={() => navigate('/my-profile')}
        />
        <div
          className={cn('my-4 mb-10', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}
        >
          <h1 className={cn('text-3xl font-bold', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>API Settings</h1>
          <p className={cn('text-xl mt-2', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>
            Manage your API keys and developer connections.
          </p>
        </div>
        {store.profile.api_key && (
          <div className="p-5 rounded-xl border border-gray-500/50">
          <section className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <p className={cn("text-2xl font-bold capitalize dark:text-white text-[#211E21]", {
                'text-white': theme === 'dark',
                'text-[#211E21]': theme !== 'dark',
              })}>
                Connect with Straico API
              </p>
              <LoadingButton
                onClick={handleOpenModal}
                variant="contained"
                color="primary"
                startIcon={
                  <PlusCircleIcon
                    style={{
                      width: '20px',
                      height: '20px',
                      border: '1.6px',
                    }}
                  />
                }
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textTransform: 'initial',
                  width: { xs: '225px', sm: '216px' },
                  height: { xs: '48px', sm: '40px' },
                  padding: '2px',
                  borderRadius: '10px',
                  ml: 3,
                  typography: {
                    fontFamily: 'Figtree, sans-serif',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                  },
                  '& .MuiButton-startIcon': {
                    ml: { xs: 1, sm: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                }}
              >
                Create New Api Key
              </LoadingButton>
              <ModalWithTextField
                open={isModalOpen}
                onClose={handleCloseModal}
                addNewKey={addNewKey}
              />
            </div>

            <p className={cn('', {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              Easily enhance your projects with our{' '}
              <strong>user-friendly API.</strong>
              Supercharge your tools with AI capabilities for faster and easier
              work. Visit our{' '}
              <a
                className="text-violet-blue cursor-pointer"
                href="https://documenter.getpostman.com/view/5900072/2s9YyzddrR"
                target="__blank"
              >
                API Documentation{' '}
              </a>{' '}
              for step-by-step guidance and support. Straico API Documentation.
            </p>
          </section>
          <div className="mt-6">
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '14px',
                bgcolor: (theme) =>
                  theme.palette.mode === 'light' ? '#ededf2' : '#1d1c1e',
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow className="border-b border-[#b0b0b0] dark:border-[#39383a]">
                    <TableCell
                      align="center"
                      style={{ width: '168px', height: '24px' }}
                    >
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell align="center" style={{ width: '281px' }}>
                      <strong>Key</strong>
                    </TableCell>
                    <TableCell align="center" style={{ width: '110px' }}>
                      <strong>Created</strong>
                    </TableCell>
                    <TableCell align="center" style={{ width: '110px' }}>
                      <strong>Last Used</strong>
                    </TableCell>
                    <TableCell align="center" style={{ width: '103px' }}>
                      <strong>Active</strong>
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{ width: '100px' }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Fila de la Main API Key */}
                  <TableRow align="center">
                    <TableCell align="center">
                      <div className="flex items-center justify-center gap-2">
                        <Tooltip
                          title="This is your main API key. It is used to authenticate your account with Straico's API."
                          arrow
                          placement="left"
                        >
                          <InformationCircleIcon className="text-cool-gray w-5 h-5 dark:text-sonic-silver" />
                        </Tooltip>
                        <span>Main API Key</span>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div
                        className={`${
                          theme === 'dark' ? 'bg-[#323232]' : 'bg-[#e1e1e5]'
                        }`}
                        style={{
                          position: 'relative',
                          width: '281px',
                          height: '36px',
                          borderRadius: '12px',
                          border: '2px solid',
                          paddingTop: '14px',
                          paddingLeft: '14px',
                          paddingRight: '40px',
                          paddingBottom: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderColor: '#787878',
                        }}
                      >
                        <input
                          type="text"
                          value={store.profile.api_key}
                          readOnly
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            border: 'none',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                          }}
                          className={`w-full border ${
                            theme === 'dark'
                              ? 'bg-[#323232] text-crystal-bell'
                              : 'bg-[#e1e1e5] text-raisin-black'
                          }  border-platinum border-solid p-[8px] rounded-md focus:border-violet-blue`}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        >
                          <ClipboardDocumentIcon
                            className="cursor-pointer w-5 h-5"
                            onClick={handleCopy}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      {new Date(store.profile.created).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <span>------</span>
                    </TableCell>
                    <TableCell align="center">
                      <span>Yes</span>
                    </TableCell>
                    <TableCell align="center">
                      <div className="flex items-center justify-center">
                        <ArrowPathIcon
                          onClick={resetApiKey}
                          style={{
                            width: '18px',
                            height: '29px',
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Dynamic API Keys */}
                  {apiKeys.map((apiKey) => (
                    <TableRow key={apiKey._id}>
                      <TableCell align="center">{apiKey.name}</TableCell>
                      <TableCell align="center">
                        <div
                          className={`${
                            theme === 'dark' ? 'bg-[#323232]' : 'bg-[#e1e1e5]'
                          }`}
                          style={{
                            position: 'relative',
                            width: '281px',
                            height: '36px',
                            borderRadius: '12px',
                            border: '2px solid',
                            paddingTop: '14px',
                            paddingLeft: '14px',
                            paddingRight: '40px',
                            paddingBottom: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderColor: '#787878',
                          }}
                        >
                          <input
                            name="api_key"
                            value={apiKey.key}
                            readOnly
                            style={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              border: 'none',
                              outline: 'none',
                              padding: 0,
                              margin: 0,
                            }}
                            className={`w-full border ${
                              theme === 'dark'
                                ? 'bg-[#323232] text-crystal-bell'
                                : 'bg-[#e1e1e5] text-raisin-black'
                            }  border-platinum border-solid p-[8px] rounded-md focus:border-violet-blue`}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <ClipboardDocumentIcon
                              className="cursor-pointer w-5 h-5"
                              onClick={(e) => handleCopy2(e, apiKey.key)}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        {new Date(apiKey.created).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        {apiKey.lastUsed
                          ? new Date(apiKey.lastUsed).toLocaleString()
                          : 'Never'}
                      </TableCell>
                      <TableCell align="center">
                        {apiKey.isActive ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell align="center">
                        <div className="flex items-center justify-center">
                          <TrashIcon
                            onClick={() => deleteKeyById(apiKey._id)}
                            style={{
                              width: '18px',
                              height: '20px',
                              cursor: 'pointer',
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <p className={cn('mt-5', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>
            Keep your API key secure - it's like a password. Lost it? Contact us
            ASAP at{' '}
            <a
              className="text-violet-blue cursor-pointer"
              href="mailto: hello@straico.com"
              target="__blank"
            >
              hello@straico.com
            </a>
          </p>
        </div>
      )}
        <div className="h-10"></div>
      </Container>
    </div>
  );
};

export default inject('store')(observer(ApiSettings));
